import { CoverLetter, Resume } from '@rio/types'
import { trackInternalEvent } from '@rio/tracking'
import { useRef, useState } from 'react'
import { loadWorker } from 'builder/modules/rendering/renderingSagas'
import RenderingConfigService from 'builder/services/RenderingConfigService'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useI18n } from 'builder/hooks/useI18n'
import PerformanceLogger from 'builder/services/PerformanceLogger'
import PdfPreview from '../PdfPreview'
import {
  BlurredTemplate,
  UnlockContainer,
  UnlockText,
  UnlockButton,
  TemplateContainer,
} from './styles'

type Props = {
  width: number
  height: number
  currentDocument: Resume | CoverLetter
  handleUnlockClick: () => void
  onPaginationChange: (state: { currentPage: number; totalPagesCount: number }) => void
  analyticsLabel: string
}

const mockPagesNumber = 1

const defaultThumbnail =
  '/assets/templates/london-439731a637642c0540010c1e00960577fa3f5914c0b0baa48f130ed0284401e0.jpg'

export const PreviewGuest = (props: Props) => {
  const { width, height, currentDocument, handleUnlockClick, onPaginationChange, analyticsLabel } =
    props

  const { resumeTemplates } = useTypedSelector(state => state.generalEditor)
  const thumbnail =
    resumeTemplates.find(t => t.id === currentDocument.template)?.thumbnail || defaultThumbnail
  const [isCsrFailed, setIsCsrFailed] = useState(false)
  const [renderNumber, setRenderNumber] = useState(1)
  const previewRef = useRef<PdfPreview>(null)
  const { i18n } = useI18n()
  const isRenderedOnce = useRef(false)

  const getPdfBuffer = async () => {
    PerformanceLogger.listen({
      name: 'client-renderer',
      op: 'rendering',
      tags: {
        renderNumber: renderNumber,
        documentType: currentDocument.type,
        template: currentDocument.template,
        locale: currentDocument.locale,
        pixelRatio: window.devicePixelRatio || 1,
        // deviceMemory is only available on some devices in HTTPS mode and fore some reason
        // it is not supported by typescript
        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/deviceMemory
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        memory: window.navigator.deviceMemory,
        viewportWidth: Math.floor(window.innerWidth / 100) * 100,
      },
    })

    setRenderNumber(renderNumber)

    const getConfig = async () => {
      PerformanceLogger.operation('loadConfig')
      const config = await RenderingConfigService.getConfig(currentDocument.locale)
      PerformanceLogger.operationEnd('loadConfig')
      return config
    }

    const getWorker = async () => {
      PerformanceLogger.operation('loadRendererWorker')
      const worker = await loadWorker()
      PerformanceLogger.operationEnd('loadRendererWorker')
      return worker
    }

    const [config, { renderWorker }] = await Promise.all([getConfig(), getWorker()])

    const generatePdfBuffer = async () => {
      PerformanceLogger.operation('rendering')
      const pdfBuffer = await renderWorker({
        document: currentDocument,
        config,
      })
      PerformanceLogger.operationEnd('rendering')
      return pdfBuffer
    }

    return await generatePdfBuffer()
  }

  const fallbackRenderer = (data: { message?: string; reason: string }) => {
    console.warn('Rendering failed. Falling back to static preview. Details:', data)

    PerformanceLogger.finish({ status: 'internal_error' })
    setIsCsrFailed(true)
    onPaginationChange({ currentPage: 1, totalPagesCount: mockPagesNumber })

    if (!isRenderedOnce.current) {
      isRenderedOnce.current = true
      trackInternalEvent('show_preview_placeholder', {
        label: analyticsLabel,
      })
    }
  }

  const handleSuccessfulRender = () => {
    PerformanceLogger.finish({ status: 'ok' })

    if (!isRenderedOnce.current) {
      isRenderedOnce.current = true
      trackInternalEvent('load_resume_preview', {
        label: analyticsLabel,
      })
    }
  }

  return !isCsrFailed ? (
    <PdfPreview
      {...{
        height,
        width,
        documentId: currentDocument.id,
        type: currentDocument.type,
        document: currentDocument,
        ref: previewRef,
        onPaginationChange,
        getPdfBuffer,
        fallbackRenderer,
        onDone: handleSuccessfulRender,
      }}
    />
  ) : (
    <TemplateContainer>
      <BlurredTemplate src={thumbnail} width={width} height={height} />
      <UnlockContainer $height={height} $width={width}>
        <UnlockText>{i18n.t('builder.sign_up.sign_up_guest_banner.description')}</UnlockText>
        <UnlockButton
          onClick={() => {
            handleUnlockClick()
            trackInternalEvent('click_provide_email')
          }}
        >
          {i18n.t('builder.sign_up.sign_up_guest_banner.button')}
        </UnlockButton>
      </UnlockContainer>
    </TemplateContainer>
  )
}
